import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useGym } from '../contexts/GymContext';
import api from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  FileText, Download, Settings2, TrendingUp, TrendingDown, 
  Target, BarChart3, Upload, X, Check, Loader2,
  Building2, Eye, List, Layers, LineChart,
  Wallet, ArrowRightLeft, Calendar, Activity, Receipt, CreditCard,
  Smartphone, Monitor, Share2, MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import { formatCurrency as formatCurrencyUtil, formatCurrencyCompact as formatCurrencyCompactUtil, formatNumber, formatPercent } from '../utils/formatters';

export const ReportsPage = () => {
  const { selectedGym } = useGym();
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [dreData, setDreData] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [lastGeneratedPdf, setLastGeneratedPdf] = useState(null); // Store last generated PDF
  const fileInputRef = useRef(null);
  
  // Report configuration
  const [config, setConfig] = useState({
    year: new Date().getFullYear(),
    startMonth: 1,
    endMonth: 12,
    // Detail level
    detailLevel: 'detailed', // 'simple' or 'detailed'
    // Format options
    pdfFormat: 'a4', // 'a4' (desktop) or 'mobile' (otimizado para celular)
    // Content options - Existentes
    includeDRE: true,
    includeExpenseCategories: true,
    includeRevenueCategories: true,
    includePerformance: true,
    // Detailed month-by-month
    monthByMonthDetail: false,
    // Performance charts (Projected vs Executed)
    includePerformanceCharts: false,
    // NOVOS - Relatórios adicionais
    includeFluxoCaixa: false,
    includeFaturadoVsRecebido: false,
    includeProjecao: false,
    includeSaudeFinanceira: false,
    includeCompromissos: false
  });

  const months = [
    { value: 1, label: 'Janeiro', short: 'Jan' },
    { value: 2, label: 'Fevereiro', short: 'Fev' },
    { value: 3, label: 'Março', short: 'Mar' },
    { value: 4, label: 'Abril', short: 'Abr' },
    { value: 5, label: 'Maio', short: 'Mai' },
    { value: 6, label: 'Junho', short: 'Jun' },
    { value: 7, label: 'Julho', short: 'Jul' },
    { value: 8, label: 'Agosto', short: 'Ago' },
    { value: 9, label: 'Setembro', short: 'Set' },
    { value: 10, label: 'Outubro', short: 'Out' },
    { value: 11, label: 'Novembro', short: 'Nov' },
    { value: 12, label: 'Dezembro', short: 'Dez' }
  ];

  const loadCompanyLogo = useCallback(async () => {
    try {
      const response = await api.get('/company/logo', { params: { gym_id: selectedGym.id } });
      setCompanyLogo(response.data.logo_base64);
    } catch (error) {
    }
  }, [selectedGym]);

  useEffect(() => {
    if (selectedGym) {
      loadCompanyLogo();
    }
  }, [selectedGym, loadCompanyLogo]);

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Por favor, selecione uma imagem válida');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('A imagem deve ter no máximo 2MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = e.target.result;
      try {
        await api.post('/company/logo', { logo_base64: base64 }, { params: { gym_id: selectedGym.id } });
        setCompanyLogo(base64);
        toast.success('Logo atualizado com sucesso!');
      } catch (error) {
        toast.error('Erro ao salvar logo');
      }
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = async () => {
    try {
      await api.delete('/company/logo', { params: { gym_id: selectedGym.id } });
      setCompanyLogo(null);
      toast.success('Logo removido');
    } catch (error) {
      toast.error('Erro ao remover logo');
    }
  };

  const [goalsData, setGoalsData] = useState(null);

  const loadDREData = async () => {
    if (!selectedGym) return;
    
    setLoading(true);
    try {
      // Load DRE data
      const dreResponse = await api.get('/reports/dre', {
        params: {
          gym_id: selectedGym.id,
          year: config.year,
          start_month: config.startMonth,
          end_month: config.endMonth
        }
      });
      setDreData(dreResponse.data);
      
      // Load goals data for performance charts
      try {
        const goalsResponse = await api.get('/goals', {
          params: {
            gym_id: selectedGym.id
          }
        });
        // Filter goals for the selected year
        const yearGoals = goalsResponse.data.filter(g => g.year === config.year);
        setGoalsData(yearGoals);
      } catch (goalsError) {
        setGoalsData([]);
      }
    } catch (error) {
      toast.error('Erro ao carregar dados do DRE');
    } finally {
      setLoading(false);
    }
  };

  // Use the centralized formatters
  const formatCurrency = formatCurrencyUtil;
  const formatCurrencyCompact = formatCurrencyCompactUtil;

  const generatePDF = async () => {
    if (!dreData) {
      toast.error('Carregue os dados primeiro');
      return;
    }

    setGenerating(true);
    
    try {
      // Determine format based on config
      const isMobileFormat = config.pdfFormat === 'mobile';
      
      // Create PDF with appropriate format
      // Mobile: narrower format (105mm x 148mm - A6) optimized for phone screens
      // Desktop: standard A4 (210mm x 297mm)
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: isMobileFormat ? [105, 200] : 'a4' // Custom mobile format: 105mm wide x 200mm tall
      });
      
      // Page dimensions based on format
      const pageWidth = isMobileFormat ? 105 : 210;
      const pageHeight = isMobileFormat ? 200 : 297;
      
      // Margins - smaller for mobile
      const marginLeft = isMobileFormat ? 6 : 12;
      const marginRight = isMobileFormat ? 6 : 12;
      const marginTop = isMobileFormat ? 8 : 12;
      const marginBottom = isMobileFormat ? 10 : 14;
      
      // Alias for cleaner code
      const contentPadding = marginLeft;
      
      // Content area
      const contentWidth = pageWidth - marginLeft - marginRight;
      const contentHeight = pageHeight - marginTop - marginBottom;
      const contentEndY = pageHeight - marginBottom;
      
      let yPos = marginTop;

      // Font sizes - smaller for mobile
      const fontSize = {
        title: isMobileFormat ? 10 : 14,
        subtitle: isMobileFormat ? 8 : 11,
        heading: isMobileFormat ? 9 : 12,
        body: isMobileFormat ? 7 : 10,
        small: isMobileFormat ? 6 : 8,
        tiny: isMobileFormat ? 5 : 7
      };

      // Premium Light Theme Color Palette
      const colors = {
        // Main colors - clean black and white
        black: [20, 20, 20],
        darkGray: [60, 60, 60],
        mediumGray: [120, 120, 120],
        lightGray: [180, 180, 180],
        veryLightGray: [240, 240, 240],
        white: [255, 255, 255],
        // Accent colors for highlights
        accentGreen: [34, 139, 34],       // Forest green
        accentRed: [180, 50, 50],          // Deep red
        accentGold: [180, 140, 50],        // Gold
        // Gradient colors for premium titles
        gradientStart: [60, 60, 60],       // Dark gray
        gradientEnd: [140, 140, 140],      // Medium gray
      };

      // Safe number formatting
      const safeNumber = (val) => {
        const num = Number(val);
        return isNaN(num) ? 0 : num;
      };

      const safeString = (val) => {
        return val != null ? String(val) : '';
      };

      // Helper functions
      const checkNewPage = (requiredSpace = 30) => {
        if (yPos + requiredSpace > contentEndY) {
          doc.addPage();
          addPageBackground();
          yPos = marginTop;
          return true;
        }
        return false;
      };

      // Calculate optimal font size based on text length and available width
      const calculateFontSize = (text, maxWidth, baseFontSize, minFontSize = 8) => {
        doc.setFontSize(baseFontSize);
        let textWidth = doc.getTextWidth(text);
        let fontSize = baseFontSize;
        
        while (textWidth > maxWidth && fontSize > minFontSize) {
          fontSize -= 1;
          doc.setFontSize(fontSize);
          textWidth = doc.getTextWidth(text);
        }
        
        return fontSize;
      };

      // Truncate text with ellipsis if too long
      const truncateText = (text, maxWidth, fontSize) => {
        doc.setFontSize(fontSize);
        let textWidth = doc.getTextWidth(text);
        
        if (textWidth <= maxWidth) return text;
        
        let truncated = text;
        while (textWidth > maxWidth && truncated.length > 3) {
          truncated = truncated.slice(0, -4) + '...';
          textWidth = doc.getTextWidth(truncated);
        }
        
        return truncated;
      };

      const addPageBackground = () => {
        // Clean white background - full page
        doc.setFillColor(...colors.white);
        doc.rect(0, 0, pageWidth, pageHeight, 'F');
      };

      const drawPremiumTitle = (text, x, y, fontSize = 14, align = 'left') => {
        // Premium title with gradient effect (simulated with shadow)
        doc.setFontSize(fontSize);
        
        // Shadow layer (lighter)
        doc.setTextColor(...colors.mediumGray);
        doc.setFont('helvetica', 'bold');
        doc.text(text, x + 0.3, y + 0.3, { align });
        
        // Main text (dark)
        doc.setTextColor(...colors.black);
        doc.text(text, x, y, { align });
      };

      const drawElegantBox = (x, y, w, h, fillColor = colors.white, borderColor = colors.lightGray) => {
        // Subtle shadow
        doc.setFillColor(230, 230, 230);
        doc.roundedRect(x + 1, y + 1, w, h, 2, 2, 'F');
        
        // Main box
        doc.setFillColor(...fillColor);
        doc.roundedRect(x, y, w, h, 2, 2, 'F');
        
        // Border
        doc.setDrawColor(...borderColor);
        doc.setLineWidth(0.3);
        doc.roundedRect(x, y, w, h, 2, 2, 'S');
      };

      const drawMetricCard = (x, y, w, h, label, value, subvalue, accentColor) => {
        try {
          // Card background
          drawElegantBox(x, y, w, h, colors.white, colors.lightGray);
          
          // Top accent line
          doc.setFillColor(...accentColor);
          doc.rect(x, y, w, 3, 'F');
          
          // Available width for text (with padding)
          const textMaxWidth = w - 16;
          
          // Label - auto-adjust
          const labelText = safeString(label).toUpperCase();
          const labelFontSize = calculateFontSize(labelText, textMaxWidth, 7, 5);
          doc.setFontSize(labelFontSize);
          doc.setTextColor(...colors.mediumGray);
          doc.setFont('helvetica', 'normal');
          doc.text(labelText, x + 8, y + 14);
          
          // Value - auto-adjust for large numbers
          const valueText = safeString(value);
          const valueFontSize = calculateFontSize(valueText, textMaxWidth, 15, 9);
          doc.setFontSize(valueFontSize);
          doc.setTextColor(...colors.black);
          doc.setFont('helvetica', 'bold');
          doc.text(valueText, x + 8, y + 27);
          
          // Subvalue - auto-adjust
          if (subvalue) {
            const subvalueText = safeString(subvalue);
            const subvalueFontSize = calculateFontSize(subvalueText, textMaxWidth, 7, 5);
            doc.setFontSize(subvalueFontSize);
            doc.setTextColor(...colors.mediumGray);
            doc.setFont('helvetica', 'normal');
            doc.text(subvalueText, x + 8, y + 35);
          }
        } catch (e) {
        }
      };

      const drawSectionHeader = (title, subtitle = '', accentColor = colors.black) => {
        try {
          checkNewPage(25);
          
          // Accent bar
          doc.setFillColor(...accentColor);
          doc.rect(marginLeft, yPos, 3, 14, 'F');
          
          // Title with premium effect
          drawPremiumTitle(safeString(title).toUpperCase(), marginLeft + 10, yPos + 10, 12);
          
          if (subtitle) {
            doc.setFontSize(8);
            doc.setTextColor(...colors.mediumGray);
            doc.setFont('helvetica', 'normal');
            doc.text(safeString(subtitle), marginLeft + 10, yPos + 16);
          }
          
          yPos += 22;
        } catch (e) {
          yPos += 22;
        }
      };

      const drawProgressBar = (x, y, w, percentage, color) => {
        try {
          const barHeight = 5;
          const pct = safeNumber(percentage);
          
          // Background
          doc.setFillColor(...colors.veryLightGray);
          doc.roundedRect(x, y, w, barHeight, 2, 2, 'F');
          
          // Progress
          const progressWidth = Math.max(0, Math.min((pct / 100) * w, w));
          if (progressWidth > 0) {
            doc.setFillColor(...color);
            doc.roundedRect(x, y, progressWidth, barHeight, 2, 2, 'F');
          }
          
          // Percentage text
          doc.setFontSize(7);
          doc.setTextColor(...colors.darkGray);
          doc.setFont('helvetica', 'bold');
          doc.text(`${pct.toFixed(0)}%`, x + w + 4, y + 4);
        } catch (e) {
        }
      };

      // ============ COVER PAGE ============
      try {
        addPageBackground();
        
        // Unified background - subtle gradient effect with no dividing line
        const headerHeight = isMobileFormat ? 50 : 85;
        doc.setFillColor(...colors.veryLightGray);
        doc.rect(0, 0, pageWidth, headerHeight, 'F');
        
        // Logo + Company name
        const logoSize = isMobileFormat ? 24 : 42;
        const companyName = safeString(dreData.company_name || 'Empresa').toUpperCase();
        
        if (companyLogo && !isMobileFormat) {
          // Desktop: Logo on the left
          try {
            doc.addImage(companyLogo, 'PNG', marginLeft + 8, 18, logoSize, logoSize);
            
            // Company name to the right of logo - auto-adjust font size
            const maxNameWidth = pageWidth - marginLeft - logoSize - 22 - marginRight - 10;
            const nameFontSize = calculateFontSize(companyName, maxNameWidth, 20, 12);
            
            doc.setFontSize(nameFontSize);
            doc.setTextColor(...colors.black);
            doc.setFont('helvetica', 'bold');
            doc.text(companyName, marginLeft + logoSize + 22, 36, { align: 'left' });
            
            // Subtitle under company name
            doc.setFontSize(9);
            doc.setTextColor(...colors.mediumGray);
            doc.setFont('helvetica', 'normal');
            doc.text('PERFORMANCE EMPRESARIAL', marginLeft + logoSize + 22, 36 + nameFontSize * 0.5 + 4);
            
          } catch (e) {
            // Centered - auto-adjust font size
            const maxNameWidth = contentWidth - 20;
            const nameFontSize = calculateFontSize(companyName, maxNameWidth, 24, 14);
            
            doc.setFontSize(nameFontSize);
            doc.setTextColor(...colors.black);
            doc.setFont('helvetica', 'bold');
            doc.text(companyName, pageWidth / 2, 38, { align: 'center' });
            
            doc.setFontSize(9);
            doc.setTextColor(...colors.mediumGray);
            doc.setFont('helvetica', 'normal');
            doc.text('PERFORMANCE EMPRESARIAL', pageWidth / 2, 50, { align: 'center' });
          }
        } else {
          // Mobile or no logo: name centered with subtitle
          const maxNameWidth = contentWidth - (isMobileFormat ? 8 : 20);
          const baseSize = isMobileFormat ? 14 : 24;
          const minSize = isMobileFormat ? 10 : 14;
          const nameFontSize = calculateFontSize(companyName, maxNameWidth, baseSize, minSize);
          
          const nameY = isMobileFormat ? 22 : 38;
          const subtitleY = isMobileFormat ? 30 : 50;
          const subtitleSize = isMobileFormat ? 6 : 9;
          
          doc.setFontSize(nameFontSize);
          doc.setTextColor(...colors.black);
          doc.setFont('helvetica', 'bold');
          doc.text(companyName, pageWidth / 2, nameY, { align: 'center' });
          
          doc.setFontSize(subtitleSize);
          doc.setTextColor(...colors.mediumGray);
          doc.setFont('helvetica', 'normal');
          doc.text('PERFORMANCE EMPRESARIAL', pageWidth / 2, subtitleY, { align: 'center' });
        }
        
        yPos = isMobileFormat ? 55 : 95;
        
        // Report type - Premium title (centered)
        const titleSize = isMobileFormat ? 9 : 13;
        drawPremiumTitle('DEMONSTRAÇÃO DO RESULTADO DO EXERCÍCIO', pageWidth / 2, yPos, titleSize, 'center');
        
        yPos += isMobileFormat ? 6 : 10;
        
        doc.setFontSize(isMobileFormat ? 6 : 9);
        doc.setTextColor(...colors.mediumGray);
        doc.setFont('helvetica', 'normal');
        doc.text('Relatório Gerencial Executivo', pageWidth / 2, yPos, { align: 'center' });
        
        yPos += isMobileFormat ? 12 : 20;
        
        // Period box - more elegant without heavy borders
        const periodBoxWidth = isMobileFormat ? 80 : 120;
        const periodBoxHeight = isMobileFormat ? 22 : 32;
        const periodBoxX = (pageWidth - periodBoxWidth) / 2;
        
        // Subtle background for period
        doc.setFillColor(250, 250, 250);
        doc.roundedRect(periodBoxX, yPos, periodBoxWidth, periodBoxHeight, 3, 3, 'F');
        doc.setDrawColor(...colors.lightGray);
        doc.setLineWidth(0.3);
        doc.roundedRect(periodBoxX, yPos, periodBoxWidth, periodBoxHeight, 3, 3, 'S');
        
        doc.setFontSize(isMobileFormat ? 5 : 7);
        doc.setTextColor(...colors.mediumGray);
        doc.text('PERÍODO DE ANÁLISE', pageWidth / 2, yPos + (isMobileFormat ? 7 : 11), { align: 'center' });
        
        const startMonth = safeString(dreData.period?.start_month_name || '');
        const endMonth = safeString(dreData.period?.end_month_name || '');
        const year = safeString(dreData.period?.year || '');
        const periodText = `${startMonth} a ${endMonth} de ${year}`;
        
        doc.setFontSize(isMobileFormat ? 8 : 12);
        doc.setTextColor(...colors.black);
        doc.setFont('helvetica', 'bold');
        doc.text(periodText.toUpperCase(), pageWidth / 2, yPos + (isMobileFormat ? 16 : 24), { align: 'center' });
        
        yPos += periodBoxHeight + (isMobileFormat ? 10 : 18);
        
        // Key metrics preview on cover
        const totalRevenue = safeNumber(dreData.summary?.total_revenue);
        const totalExpenses = safeNumber(dreData.summary?.total_expenses);
        const totalProfit = safeNumber(dreData.summary?.total_profit);
        const overallMargin = safeNumber(dreData.summary?.overall_margin);
        
        if (isMobileFormat) {
          // Mobile: metrics stacked vertically in 2 columns
          const metricWidth = (contentWidth - 6) / 2;
          const metricHeight = 28;
          
          drawMetricCard(marginLeft, yPos, metricWidth, metricHeight, 
            'Faturamento', formatCurrencyCompact(totalRevenue), 
            'Total', colors.accentGreen);
          
          drawMetricCard(marginLeft + metricWidth + 6, yPos, metricWidth, metricHeight,
            'Despesas', formatCurrencyCompact(totalExpenses),
            'Total', colors.accentRed);
          
          yPos += metricHeight + 6;
          
          const profitColor = totalProfit >= 0 ? colors.accentGreen : colors.accentRed;
          const profitMetricWidth = contentWidth;
          drawMetricCard(marginLeft, yPos, profitMetricWidth, metricHeight,
            'Resultado', formatCurrencyCompact(totalProfit),
            `Margem: ${overallMargin.toFixed(1)}%`, profitColor);
        } else {
          // Desktop: 3 metrics in a row
          const metricWidth = 55;
          const metricHeight = 44;
          const metricsStartX = (pageWidth - (metricWidth * 3 + 16)) / 2;
          
          drawMetricCard(metricsStartX, yPos, metricWidth, metricHeight, 
            'Faturamento', formatCurrencyCompact(totalRevenue), 
            'Total do período', colors.accentGreen);
          
          drawMetricCard(metricsStartX + metricWidth + 8, yPos, metricWidth, metricHeight,
            'Despesas', formatCurrencyCompact(totalExpenses),
            'Total do período', colors.accentRed);
          
          const profitColor = totalProfit >= 0 ? colors.accentGreen : colors.accentRed;
          drawMetricCard(metricsStartX + (metricWidth + 8) * 2, yPos, metricWidth, metricHeight,
            'Resultado', formatCurrencyCompact(totalProfit),
            `Margem: ${overallMargin.toFixed(1)}%`, profitColor);
        }

        // Footer on cover - positioned at bottom
        doc.setFontSize(isMobileFormat ? 5 : 7);
        doc.setTextColor(...colors.mediumGray);
        doc.text(`Gerado em ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}`, 
          pageWidth / 2, contentEndY - 3, { align: 'center' });
        doc.text('Alvo - Sistema de Performance Empresarial', pageWidth / 2, contentEndY + 2, { align: 'center' });
        
      } catch (e) {
      }

      // ============ EXECUTIVE SUMMARY PAGE ============
      try {
        doc.addPage();
        addPageBackground();
        yPos = marginTop;

        // Page header - elegant light design (full width)
        doc.setFillColor(...colors.veryLightGray);
        doc.rect(0, 0, pageWidth, 30, 'F');
        doc.setFillColor(...colors.darkGray);
        doc.rect(0, 28, pageWidth, 2, 'F');
        
        drawPremiumTitle('RESUMO EXECUTIVO', marginLeft, 18, 14);
        
        doc.setFontSize(8);
        doc.setTextColor(...colors.mediumGray);
        doc.text(safeString(dreData.company_name), pageWidth - marginRight, 18, { align: 'right' });
        
        yPos = 38;

        // Large metric cards - full width using margins
        const cardWidth = (contentWidth - 10) / 2;
        const cardHeight = 46;
        
        const totalRevenue = safeNumber(dreData.summary?.total_revenue);
        const totalExpenses = safeNumber(dreData.summary?.total_expenses);
        const totalProfit = safeNumber(dreData.summary?.total_profit);
        const overallMargin = safeNumber(dreData.summary?.overall_margin);
        
        // Revenue card
        drawElegantBox(marginLeft, yPos, cardWidth, cardHeight, colors.white, colors.lightGray);
        doc.setFillColor(...colors.accentGreen);
        doc.rect(marginLeft, yPos, cardWidth, 3, 'F');
        
        doc.setFontSize(8);
        doc.setTextColor(...colors.mediumGray);
        doc.text('FATURAMENTO BRUTO', marginLeft + 10, yPos + 15);
        doc.setFontSize(18);
        doc.setTextColor(...colors.black);
        doc.setFont('helvetica', 'bold');
        doc.text(formatCurrency(totalRevenue), marginLeft + 10, yPos + 34);
        
        // Expense card
        drawElegantBox(marginLeft + cardWidth + 10, yPos, cardWidth, cardHeight, colors.white, colors.lightGray);
        doc.setFillColor(...colors.accentRed);
        doc.rect(marginLeft + cardWidth + 10, yPos, cardWidth, 3, 'F');
        
        doc.setFontSize(8);
        doc.setTextColor(...colors.mediumGray);
        doc.setFont('helvetica', 'normal');
        doc.text('DESPESAS OPERACIONAIS', marginLeft + cardWidth + 20, yPos + 15);
        doc.setFontSize(18);
        doc.setTextColor(...colors.black);
        doc.setFont('helvetica', 'bold');
        doc.text(formatCurrency(totalExpenses), marginLeft + cardWidth + 20, yPos + 34);
        
        yPos += cardHeight + 8;
        
        // Result card - full width
        const resultAccent = totalProfit >= 0 ? colors.accentGreen : colors.accentRed;
        drawElegantBox(marginLeft, yPos, contentWidth, 42, colors.white, colors.lightGray);
        doc.setFillColor(...resultAccent);
        doc.rect(marginLeft, yPos, contentWidth, 3, 'F');
        
        doc.setFontSize(8);
        doc.setTextColor(...colors.mediumGray);
        doc.setFont('helvetica', 'normal');
        doc.text('RESULTADO LÍQUIDO DO PERÍODO', marginLeft + 10, yPos + 14);
        
        doc.setFontSize(24);
        doc.setTextColor(...colors.black);
        doc.setFont('helvetica', 'bold');
        doc.text(formatCurrency(totalProfit), marginLeft + 10, yPos + 32);
        
        // Margin indicator
        doc.setFontSize(11);
        doc.setTextColor(...colors.darkGray);
        doc.text(`MARGEM: ${overallMargin.toFixed(1)}%`, pageWidth - marginRight - 10, yPos + 26, { align: 'right' });
        
        yPos += 52;
        
      } catch (e) {
      }

      // ============ DRE TABLE ============
      if (config.includeDRE) {
        try {
          // Start DRE on a new dedicated page to ensure it fits in one page
          doc.addPage();
          addPageBackground();
          yPos = marginTop;

          // Page header for DRE
          const headerHeight = isMobileFormat ? 20 : 30;
          doc.setFillColor(...colors.veryLightGray);
          doc.rect(0, 0, pageWidth, headerHeight, 'F');
          doc.setFillColor(...colors.darkGray);
          doc.rect(0, headerHeight - 2, pageWidth, 2, 'F');
          
          drawPremiumTitle('DRE MENSAL CONSOLIDADO', marginLeft, isMobileFormat ? 12 : 18, isMobileFormat ? 10 : 14);
          
          doc.setFontSize(isMobileFormat ? 6 : 8);
          doc.setTextColor(...colors.mediumGray);
          doc.text(safeString(dreData.company_name), pageWidth - marginRight, isMobileFormat ? 12 : 18, { align: 'right' });
          
          yPos = headerHeight + 10;

          const monthlyData = dreData.monthly_data || [];
          
          if (isMobileFormat) {
            // Mobile: simplified table with fewer columns
            const tableData = monthlyData.map(m => [
              safeString(m.month_short).toUpperCase(),
              formatCurrencyCompact(safeNumber(m.revenue)),
              formatCurrencyCompact(safeNumber(m.profit)),
              `${safeNumber(m.margin).toFixed(0)}%`
            ]);

            const totalRevenue = safeNumber(dreData.summary?.total_revenue);
            const totalProfit = safeNumber(dreData.summary?.total_profit);
            const overallMargin = safeNumber(dreData.summary?.overall_margin);

            autoTable(doc, {
              startY: yPos,
              head: [['MÊS', 'FAT.', 'RESULT.', '%']],
              body: tableData,
              foot: [['TOTAL', formatCurrencyCompact(totalRevenue), 
                      formatCurrencyCompact(totalProfit),
                      `${overallMargin.toFixed(0)}%`]],
              theme: 'plain',
              styles: {
                fillColor: colors.white,
                textColor: colors.darkGray,
                fontSize: 6,
                cellPadding: 2,
                lineColor: colors.lightGray,
                lineWidth: 0.2,
                overflow: 'ellipsize',
              },
              headStyles: {
                fillColor: colors.veryLightGray,
                textColor: colors.black,
                fontStyle: 'bold',
                fontSize: 5,
              },
              footStyles: {
                fillColor: colors.veryLightGray,
                textColor: colors.black,
                fontStyle: 'bold',
                fontSize: 7,
              },
              columnStyles: {
                0: { cellWidth: 18, fontStyle: 'bold', textColor: colors.black },
                1: { halign: 'right', textColor: colors.accentGreen },
                2: { halign: 'right', textColor: colors.black, fontStyle: 'bold' },
                3: { halign: 'center', cellWidth: 14, textColor: colors.darkGray }
              },
              margin: { left: marginLeft, right: marginRight },
              tableLineColor: colors.lightGray,
              tableLineWidth: 0.2,
              showHead: 'firstPage',
              showFoot: 'lastPage',
            });
          } else {
            // Desktop: full table
            const tableData = monthlyData.map(m => [
              safeString(m.month_short).toUpperCase(),
              formatCurrency(safeNumber(m.revenue)),
              formatCurrency(safeNumber(m.expenses)),
              formatCurrency(safeNumber(m.profit)),
              `${safeNumber(m.margin).toFixed(1)}%`
            ]);

            const totalRevenue = safeNumber(dreData.summary?.total_revenue);
            const totalExpenses = safeNumber(dreData.summary?.total_expenses);
            const totalProfit = safeNumber(dreData.summary?.total_profit);
            const overallMargin = safeNumber(dreData.summary?.overall_margin);

            autoTable(doc, {
              startY: yPos,
              head: [['MÊS', 'FATURAMENTO', 'DESPESAS', 'RESULTADO', 'MARGEM']],
              body: tableData,
              foot: [['TOTAL', formatCurrency(totalRevenue), 
                      formatCurrency(totalExpenses),
                      formatCurrency(totalProfit),
                      `${overallMargin.toFixed(1)}%`]],
              theme: 'plain',
              styles: {
                fillColor: colors.white,
                textColor: colors.darkGray,
                fontSize: 9,
                cellPadding: 5,
                lineColor: colors.lightGray,
                lineWidth: 0.2,
                overflow: 'ellipsize',
              },
              headStyles: {
                fillColor: colors.veryLightGray,
                textColor: colors.black,
                fontStyle: 'bold',
                fontSize: 8,
              },
              footStyles: {
                fillColor: colors.veryLightGray,
                textColor: colors.black,
                fontStyle: 'bold',
                fontSize: 10,
              },
              columnStyles: {
                0: { cellWidth: 30, fontStyle: 'bold', textColor: colors.black },
                1: { halign: 'right', textColor: colors.accentGreen },
                2: { halign: 'right', textColor: colors.accentRed },
                3: { halign: 'right', textColor: colors.black, fontStyle: 'bold' },
                4: { halign: 'center', cellWidth: 28, textColor: colors.darkGray }
              },
              margin: { left: marginLeft, right: marginRight },
              tableLineColor: colors.lightGray,
              tableLineWidth: 0.2,
              showHead: 'firstPage',
              showFoot: 'lastPage',
            });
          }

          yPos = doc.lastAutoTable.finalY + 12;
        } catch (e) {
        }
      }

      // ============ EXPENSE CATEGORIES ============
      if (config.includeExpenseCategories) {
        try {
          const expenseCategories = dreData.summary?.expense_categories || {};
          if (Object.keys(expenseCategories).length > 0) {
            checkNewPage(80);
            drawSectionHeader('Composição de Despesas', 'Análise por categoria', colors.accentRed);

            const totalExpenses = safeNumber(dreData.summary?.total_expenses);
            const expenseEntries = Object.entries(expenseCategories)
              .map(([k, v]) => [safeString(k), safeNumber(v)])
              .sort((a, b) => b[1] - a[1]);
            
            expenseEntries.slice(0, 8).forEach(([category, amount]) => {
              const percentage = totalExpenses > 0 ? (amount / totalExpenses * 100) : 0;
              const barWidth = 80;
              
              checkNewPage(16);
              
              // Category name
              doc.setFontSize(8);
              doc.setTextColor(...colors.black);
              doc.setFont('helvetica', 'bold');
              doc.text(category.toUpperCase(), marginLeft + 5, yPos + 4);
              
              // Amount
              doc.setTextColor(...colors.accentRed);
              doc.text(formatCurrency(amount), marginLeft + 80, yPos + 4);
              
              // Progress bar
              drawProgressBar(marginLeft + 115, yPos, barWidth, percentage, colors.accentRed);
              
              yPos += 13;
            });
            
            yPos += 10;
          }
        } catch (e) {
        }
      }

      // ============ REVENUE SOURCES ============
      if (config.includeRevenueCategories) {
        try {
          const revenueSources = dreData.summary?.revenue_sources || {};
          if (Object.keys(revenueSources).length > 0) {
            checkNewPage(80);
            drawSectionHeader('Composição de Receitas', 'Análise por origem', colors.accentGreen);

            const totalRevenue = safeNumber(dreData.summary?.total_revenue);
            const revenueEntries = Object.entries(revenueSources)
              .map(([k, v]) => [safeString(k), safeNumber(v)])
              .sort((a, b) => b[1] - a[1]);
            
            revenueEntries.slice(0, 8).forEach(([source, amount]) => {
              const percentage = totalRevenue > 0 ? (amount / totalRevenue * 100) : 0;
              const barWidth = 80;
              
              checkNewPage(16);
              
              doc.setFontSize(8);
              doc.setTextColor(...colors.black);
              doc.setFont('helvetica', 'bold');
              doc.text(source.toUpperCase(), marginLeft + 5, yPos + 4);
              
              doc.setTextColor(...colors.accentGreen);
              doc.text(formatCurrency(amount), marginLeft + 80, yPos + 4);
              
              drawProgressBar(marginLeft + 115, yPos, barWidth, percentage, colors.accentGreen);
              
              yPos += 13;
            });
            
            yPos += 10;
          }
        } catch (e) {
        }
      }

      // ============ MONTH BY MONTH DETAIL ============
      if (config.detailLevel === 'detailed' && config.monthByMonthDetail) {
        try {
          const monthlyData = dreData.monthly_data || [];
          monthlyData.forEach((month) => {
            try {
              const monthRevenue = safeNumber(month.revenue);
              const monthExpenses = safeNumber(month.expenses);
              const hasData = monthRevenue > 0 || monthExpenses > 0;
              
              doc.addPage();
              addPageBackground();
              
              // Month header - clean light design (full width)
              doc.setFillColor(...colors.veryLightGray);
              doc.rect(0, 0, pageWidth, 36, 'F');
              doc.setFillColor(...colors.accentGold);
              doc.rect(0, 34, pageWidth, 2, 'F');
              
              const monthName = safeString(month.month);
              drawPremiumTitle(monthName.toUpperCase(), marginLeft, 20, 18);
              
              doc.setFontSize(11);
              doc.setTextColor(...colors.accentGold);
              doc.setFont('helvetica', 'bold');
              doc.text(safeString(dreData.period?.year), marginLeft, 30);
              
              doc.setFontSize(8);
              doc.setTextColor(...colors.mediumGray);
              doc.setFont('helvetica', 'normal');
              doc.text(safeString(dreData.company_name), pageWidth - marginRight, 20, { align: 'right' });
              
              yPos = 44;

              // Metric cards for the month - using margins
              const mCardWidth = (contentWidth - 15) / 4;
              const mCardHeight = 36;
              
              const monthProfit = safeNumber(month.profit);
              const monthMargin = safeNumber(month.margin);
              
              drawMetricCard(marginLeft, yPos, mCardWidth, mCardHeight,
                'Faturamento', formatCurrency(monthRevenue), null, colors.accentGreen);
              
              drawMetricCard(marginLeft + mCardWidth + 5, yPos, mCardWidth, mCardHeight,
                'Despesas', formatCurrency(monthExpenses), null, colors.accentRed);
              
              const monthProfitColor = monthProfit >= 0 ? colors.accentGreen : colors.accentRed;
              drawMetricCard(marginLeft + (mCardWidth + 5) * 2, yPos, mCardWidth, mCardHeight,
                'Resultado', formatCurrency(monthProfit), null, monthProfitColor);
              
              drawMetricCard(marginLeft + (mCardWidth + 5) * 3, yPos, mCardWidth, mCardHeight,
                'Margem', `${monthMargin.toFixed(1)}%`, null, colors.accentGold);
              
              yPos += mCardHeight + 12;

              if (!hasData) {
                // Show "no data" message
                doc.setFontSize(10);
                doc.setTextColor(...colors.mediumGray);
                doc.text('Nenhum dado registrado para este mês', pageWidth / 2, yPos + 20, { align: 'center' });
                return;
              }

              // Categories side by side - using margins
              const halfWidth = (contentWidth - 10) / 2;
              
              // Expense categories
              const expenseCategories = month.expense_by_category || {};
              if (config.includeExpenseCategories && Object.keys(expenseCategories).length > 0) {
                doc.setFillColor(...colors.accentRed);
                doc.rect(marginLeft, yPos, 3, 12, 'F');
                doc.setFontSize(10);
                doc.setTextColor(...colors.black);
                doc.setFont('helvetica', 'bold');
                doc.text('DESPESAS', marginLeft + 8, yPos + 8);
                
                let expY = yPos + 18;
                Object.entries(expenseCategories)
                  .map(([k, v]) => [safeString(k), safeNumber(v)])
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 6)
                  .forEach(([cat, amt]) => {
                    const pct = monthExpenses > 0 ? (amt / monthExpenses * 100) : 0;
                    doc.setFontSize(7);
                    doc.setTextColor(...colors.darkGray);
                    doc.setFont('helvetica', 'normal');
                    doc.text(cat, marginLeft + 5, expY);
                    doc.setTextColor(...colors.accentRed);
                    doc.text(formatCurrency(amt), marginLeft + halfWidth - 25, expY, { align: 'right' });
                    doc.setTextColor(...colors.mediumGray);
                    doc.text(`${pct.toFixed(0)}%`, marginLeft + halfWidth - 5, expY, { align: 'right' });
                    expY += 10;
                  });
              }

              // Revenue sources
              const revenueSources = month.revenue_by_source || {};
              if (config.includeRevenueCategories && Object.keys(revenueSources).length > 0) {
                doc.setFillColor(...colors.accentGreen);
                doc.rect(marginLeft + halfWidth + 10, yPos, 3, 12, 'F');
                doc.setFontSize(10);
                doc.setTextColor(...colors.black);
                doc.setFont('helvetica', 'bold');
                doc.text('RECEITAS', marginLeft + halfWidth + 18, yPos + 8);
                
                let revY = yPos + 18;
                Object.entries(revenueSources)
                  .map(([k, v]) => [safeString(k), safeNumber(v)])
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 6)
                  .forEach(([src, amt]) => {
                    const pct = monthRevenue > 0 ? (amt / monthRevenue * 100) : 0;
                    doc.setFontSize(7);
                    doc.setTextColor(...colors.darkGray);
                    doc.setFont('helvetica', 'normal');
                    doc.text(src, marginLeft + halfWidth + 15, revY);
                    doc.setTextColor(...colors.accentGreen);
                    doc.text(formatCurrency(amt), pageWidth - marginRight - 25, revY, { align: 'right' });
                    doc.setTextColor(...colors.mediumGray);
                    doc.text(`${pct.toFixed(0)}%`, pageWidth - marginRight - 5, revY, { align: 'right' });
                    revY += 10;
                  });
              }
            } catch (monthError) {
            }
          });
        } catch (e) {
        }
      }

      // ============ PERFORMANCE CHARTS (METAS DO APP) ============
      if (config.includePerformanceCharts) {
        try {
          // Check if there's any performance data
          const monthlyData = dreData.monthly_data || [];
          const monthsWithPerformance = monthlyData.filter(m => m.goals_total > 0);
          
          if (monthsWithPerformance.length > 0) {
            doc.addPage();
            addPageBackground();
            yPos = marginTop;

            // Page header
            doc.setFillColor(...colors.veryLightGray);
            doc.rect(0, 0, pageWidth, 30, 'F');
            doc.setFillColor(...colors.accentGold);
            doc.rect(0, 28, pageWidth, 2, 'F');
            
            drawPremiumTitle('PERFORMANCE DE METAS', marginLeft, 18, 14);
            
            doc.setFontSize(8);
            doc.setTextColor(...colors.mediumGray);
            doc.text(safeString(dreData.company_name), pageWidth - marginRight, 18, { align: 'right' });
            
            yPos = 42;

            // Summary cards
            const totalGoals = monthlyData.reduce((sum, m) => sum + safeNumber(m.goals_total), 0);
            const achievedGoals = monthlyData.reduce((sum, m) => sum + safeNumber(m.goals_achieved), 0);
            const overallPerformance = totalGoals > 0 ? (achievedGoals / totalGoals * 100) : 0;
            
            // Performance summary cards
            const cardWidth = (contentWidth - 20) / 3;
            const cardHeight = 40;
            
            // Total goals card
            drawElegantBox(marginLeft, yPos, cardWidth, cardHeight, colors.white, colors.lightGray);
            doc.setFillColor(...colors.accentGold);
            doc.rect(marginLeft, yPos, cardWidth, 3, 'F');
            doc.setFontSize(8);
            doc.setTextColor(...colors.mediumGray);
            doc.text('TOTAL DE METAS', marginLeft + 10, yPos + 14);
            doc.setFontSize(20);
            doc.setTextColor(...colors.black);
            doc.setFont('helvetica', 'bold');
            doc.text(String(totalGoals), marginLeft + 10, yPos + 32);
            
            // Achieved goals card
            drawElegantBox(marginLeft + cardWidth + 10, yPos, cardWidth, cardHeight, colors.white, colors.lightGray);
            doc.setFillColor(...colors.accentGreen);
            doc.rect(marginLeft + cardWidth + 10, yPos, cardWidth, 3, 'F');
            doc.setFontSize(8);
            doc.setTextColor(...colors.mediumGray);
            doc.setFont('helvetica', 'normal');
            doc.text('METAS ATINGIDAS', marginLeft + cardWidth + 20, yPos + 14);
            doc.setFontSize(20);
            doc.setTextColor(...colors.accentGreen);
            doc.setFont('helvetica', 'bold');
            doc.text(String(achievedGoals), marginLeft + cardWidth + 20, yPos + 32);
            
            // Performance percentage card
            const perfColor = overallPerformance >= 80 ? colors.accentGreen : 
                             overallPerformance >= 50 ? colors.accentGold : colors.accentRed;
            drawElegantBox(marginLeft + (cardWidth + 10) * 2, yPos, cardWidth, cardHeight, colors.white, colors.lightGray);
            doc.setFillColor(...perfColor);
            doc.rect(marginLeft + (cardWidth + 10) * 2, yPos, cardWidth, 3, 'F');
            doc.setFontSize(8);
            doc.setTextColor(...colors.mediumGray);
            doc.setFont('helvetica', 'normal');
            doc.text('PERFORMANCE GERAL', marginLeft + (cardWidth + 10) * 2 + 10, yPos + 14);
            doc.setFontSize(20);
            doc.setTextColor(...perfColor);
            doc.setFont('helvetica', 'bold');
            doc.text(`${overallPerformance.toFixed(1)}%`, marginLeft + (cardWidth + 10) * 2 + 10, yPos + 32);
            
            yPos += cardHeight + 20;

            // Performance bar chart
            doc.setFontSize(10);
            doc.setTextColor(...colors.black);
            doc.setFont('helvetica', 'bold');
            doc.text('PERFORMANCE MENSAL', marginLeft, yPos);
            yPos += 8;

            const chartWidth = contentWidth;
            const chartHeight = 70;
            const barGroupWidth = chartWidth / monthlyData.length;
            const barWidth = barGroupWidth * 0.6;
            
            const chartStartY = yPos;
            const chartEndY = yPos + chartHeight;
            
            // Draw grid lines and labels (0%, 25%, 50%, 75%, 100%)
            for (let i = 0; i <= 4; i++) {
              const gridY = chartStartY + (chartHeight * i / 4);
              const percentage = 100 - (i * 25);
              
              doc.setDrawColor(...colors.lightGray);
              doc.setLineWidth(0.1);
              doc.line(marginLeft, gridY, marginLeft + chartWidth, gridY);
              
              doc.setFontSize(6);
              doc.setTextColor(...colors.mediumGray);
              doc.setFont('helvetica', 'normal');
              doc.text(`${percentage}%`, marginLeft - 2, gridY + 1, { align: 'right' });
            }

            // Draw bars for each month
            monthlyData.forEach((monthData, index) => {
              const performance = safeNumber(monthData.performance);
              const hasGoals = safeNumber(monthData.goals_total) > 0;
              const barX = marginLeft + (barGroupWidth * index) + (barGroupWidth - barWidth) / 2;
              
              if (hasGoals) {
                // Calculate bar height (performance is 0-100%)
                const barHeight = (performance / 100) * chartHeight;
                
                // Choose color based on performance
                const barColor = performance >= 80 ? colors.accentGreen : 
                                performance >= 50 ? colors.accentGold : colors.accentRed;
                
                // Draw bar
                doc.setFillColor(...barColor);
                doc.roundedRect(barX, chartEndY - barHeight, barWidth, barHeight, 1, 1, 'F');
                
                // Performance value on top of bar
                if (barHeight > 10) {
                  doc.setFontSize(7);
                  doc.setTextColor(...colors.white);
                  doc.setFont('helvetica', 'bold');
                  doc.text(`${performance.toFixed(0)}%`, barX + barWidth / 2, chartEndY - barHeight + 8, { align: 'center' });
                }
              } else {
                // No goals - show empty indicator
                doc.setFillColor(...colors.veryLightGray);
                doc.roundedRect(barX, chartEndY - 5, barWidth, 5, 1, 1, 'F');
              }
              
              // Month label
              doc.setFontSize(7);
              doc.setTextColor(...colors.darkGray);
              doc.setFont('helvetica', 'bold');
              doc.text(safeString(monthData.month_short).toUpperCase(), barX + barWidth / 2, chartEndY + 6, { align: 'center' });
            });

            yPos = chartEndY + 15;

            // Legend
            doc.setFillColor(...colors.accentGreen);
            doc.rect(marginLeft, yPos, 8, 4, 'F');
            doc.setFontSize(7);
            doc.setTextColor(...colors.darkGray);
            doc.setFont('helvetica', 'normal');
            doc.text('≥80% Excelente', marginLeft + 12, yPos + 3);
            
            doc.setFillColor(...colors.accentGold);
            doc.rect(marginLeft + 60, yPos, 8, 4, 'F');
            doc.text('50-79% Bom', marginLeft + 72, yPos + 3);
            
            doc.setFillColor(...colors.accentRed);
            doc.rect(marginLeft + 115, yPos, 8, 4, 'F');
            doc.text('<50% Atenção', marginLeft + 127, yPos + 3);

            // ===== NEW PAGE FOR DETAILED TABLE =====
            doc.addPage();
            addPageBackground();
            yPos = marginTop;

            // Page header for detailed table
            doc.setFillColor(...colors.veryLightGray);
            doc.rect(0, 0, pageWidth, 30, 'F');
            doc.setFillColor(...colors.accentGold);
            doc.rect(0, 28, pageWidth, 2, 'F');
            
            drawPremiumTitle('DETALHAMENTO DE PERFORMANCE', marginLeft, 18, 14);
            
            doc.setFontSize(8);
            doc.setTextColor(...colors.mediumGray);
            doc.text(safeString(dreData.company_name), pageWidth - marginRight, 18, { align: 'right' });
            
            yPos = 42;

            const tableData = monthlyData.map(m => {
              const perf = safeNumber(m.performance);
              const status = perf >= 80 ? '✓ Excelente' : perf >= 50 ? '○ Bom' : '✗ Atenção';
              return [
                safeString(m.month_short).toUpperCase(),
                String(safeNumber(m.goals_total)),
                String(safeNumber(m.goals_achieved)),
                `${perf.toFixed(1)}%`,
                status
              ];
            });

            autoTable(doc, {
              startY: yPos,
              head: [['MÊS', 'TOTAL METAS', 'ATINGIDAS', 'PERFORMANCE', 'STATUS']],
              body: tableData,
              foot: [[
                'TOTAL',
                String(totalGoals),
                String(achievedGoals),
                `${overallPerformance.toFixed(1)}%`,
                overallPerformance >= 80 ? '✓ Excelente' : overallPerformance >= 50 ? '○ Bom' : '✗ Atenção'
              ]],
              theme: 'plain',
              styles: {
                fillColor: colors.white,
                textColor: colors.darkGray,
                fontSize: 9,
                cellPadding: 5,
                lineColor: colors.lightGray,
                lineWidth: 0.2,
                overflow: 'ellipsize', // Auto-truncate long text
              },
              headStyles: {
                fillColor: colors.veryLightGray,
                textColor: colors.black,
                fontStyle: 'bold',
                fontSize: 8,
              },
              footStyles: {
                fillColor: colors.veryLightGray,
                textColor: colors.black,
                fontStyle: 'bold',
                fontSize: 10,
              },
              columnStyles: {
                0: { cellWidth: 30, fontStyle: 'bold', textColor: colors.black },
                1: { halign: 'center', cellWidth: 35 },
                2: { halign: 'center', cellWidth: 35, textColor: colors.accentGreen },
                3: { halign: 'center', cellWidth: 35 },
                4: { halign: 'center', cellWidth: 40 }
              },
              margin: { left: marginLeft, right: marginRight },
              // Prevent page break within the table
              showHead: 'firstPage',
              showFoot: 'lastPage',
            });

            yPos = doc.lastAutoTable.finalY + 15;
          }
        } catch (e) {
        }
      }

      // ============ FOOTER ON ALL PAGES ============
      try {
        const totalPages = doc.internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
          doc.setPage(i);
          
          // Bottom bar - clean light design (full width)
          doc.setFillColor(...colors.veryLightGray);
          doc.rect(0, pageHeight - 12, pageWidth, 12, 'F');
          doc.setFillColor(...colors.darkGray);
          doc.rect(0, pageHeight - 12, pageWidth, 0.5, 'F');
          
          doc.setFontSize(6);
          doc.setTextColor(...colors.mediumGray);
          doc.text(`Página ${i} de ${totalPages}`, pageWidth / 2, pageHeight - 5, { align: 'center' });
          doc.text('ALVO - Performance Empresarial', marginLeft, pageHeight - 5);
          doc.text(new Date().toLocaleDateString('pt-BR'), pageWidth - marginRight, pageHeight - 5, { align: 'right' });
        }
      } catch (e) {
      }

      // Save PDF - Force download with blob
      const companyName = safeString(dreData.company_name || 'Empresa').replace(/\s+/g, '_');
      const fileName = `DRE_${companyName}_${config.year}.pdf`;
      
      const pdfBlob = doc.output('blob');
      const url = window.URL.createObjectURL(pdfBlob);
      
      // Store the PDF blob for sharing later
      setLastGeneratedPdf({
        blob: pdfBlob,
        fileName: fileName,
        url: url,
        companyName: dreData.company_name || 'Empresa',
        period: `${dreData.period?.start_month_name || ''} a ${dreData.period?.end_month_name || ''} de ${dreData.period?.year || ''}`
      });
      
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      
      setTimeout(() => {
        link.click();
        
        setTimeout(() => {
          document.body.removeChild(link);
          // Don't revoke URL immediately - keep for sharing
        }, 1000);
      }, 100);
      
      toast.success('Relatório gerado com sucesso!');
      
    } catch (error) {
      toast.error(`Erro ao gerar PDF: ${error.message}`);
    } finally {
      setGenerating(false);
    }
  };

  // Share PDF via WhatsApp
  const shareViaWhatsApp = async () => {
    if (!lastGeneratedPdf) {
      toast.error('Gere o relatório primeiro antes de compartilhar');
      return;
    }

    try {
      // Check if Web Share API is available and supports files (mobile)
      if (navigator.share && navigator.canShare) {
        const file = new File([lastGeneratedPdf.blob], lastGeneratedPdf.fileName, { type: 'application/pdf' });
        
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: `Relatório DRE - ${lastGeneratedPdf.companyName}`,
            text: `📊 Relatório DRE\n🏢 ${lastGeneratedPdf.companyName}\n📅 Período: ${lastGeneratedPdf.period}`,
            files: [file]
          });
          toast.success('Compartilhamento iniciado!');
          return;
        }
      }

      // Fallback: Open WhatsApp with message (user can attach file manually)
      const message = encodeURIComponent(
        `📊 *Relatório DRE - ${lastGeneratedPdf.companyName}*\n\n` +
        `📅 Período: ${lastGeneratedPdf.period}\n\n` +
        `_O relatório foi gerado e baixado. Anexe o arquivo PDF a esta conversa._`
      );
      
      // Detect if mobile
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      if (isMobile) {
        // Open WhatsApp app
        window.open(`whatsapp://send?text=${message}`, '_blank');
      } else {
        // Open WhatsApp Web
        window.open(`https://web.whatsapp.com/send?text=${message}`, '_blank');
      }
      
      toast.success('WhatsApp aberto! Anexe o PDF baixado à conversa.');
      
    } catch (error) {
      if (error.name !== 'AbortError') {
        toast.error('Erro ao compartilhar. Tente novamente.');
      }
    }
  };


  return (
    <DashboardLayout>
      <div className="space-y-6 overflow-x-hidden" data-testid="reports-page">
        {/* Header com saudação e filtros */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-2">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-white">
              Relatórios Gerenciais
            </h1>
            <p className="text-zinc-400 mt-2 text-base">Gere DRE e relatórios financeiros personalizados em PDF</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
          {/* Configuration Panel */}
          <div className="lg:col-span-1 space-y-4 lg:space-y-6">
            {/* Logo Upload */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader className="pb-2 sm:pb-3">
                <CardTitle className="text-sm sm:text-base text-white flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-zinc-800/50">
                    <Building2 className="w-4 h-4 text-emerald-400" />
                  </div>
                  Logo da Empresa
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {companyLogo ? (
                    <div className="relative">
                      <div className="w-full h-20 sm:h-24 rounded-lg border border-white/10 bg-black/30 flex items-center justify-center overflow-hidden">
                        <img 
                          src={companyLogo} 
                          alt="Logo" 
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                      <button
                        onClick={handleRemoveLogo}
                        className="absolute top-2 right-2 p-1.5 rounded-full bg-red-500/80 hover:bg-red-500 transition-colors active:scale-95"
                      >
                        <X size={12} className="text-white" />
                      </button>
                    </div>
                  ) : (
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full h-24 rounded-lg border-2 border-dashed border-white/20 hover:border-[#22C55E]/50 bg-black/20 flex flex-col items-center justify-center cursor-pointer transition-colors"
                    >
                      <Upload className="text-zinc-500 mb-1" size={20} />
                      <span className="text-xs text-zinc-500">Clique para enviar</span>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Period Settings */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-white flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-zinc-800/50">
                    <Settings2 className="w-4 h-4 text-emerald-400" />
                  </div>
                  Período
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-zinc-400 uppercase tracking-wider mb-1 block">Ano</label>
                  <select
                    value={config.year}
                    onChange={(e) => setConfig({ ...config, year: parseInt(e.target.value) })}
                    className="h-10 px-4 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-emerald-500 focus:outline-none w-full"
                  >
                    {[2024, 2025, 2026].map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-medium text-zinc-400 uppercase tracking-wider mb-1 block">De</label>
                    <select
                      value={config.startMonth}
                      onChange={(e) => setConfig({ ...config, startMonth: parseInt(e.target.value) })}
                      className="h-10 px-4 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-emerald-500 focus:outline-none w-full"
                    >
                      {months.map(m => (
                        <option key={m.value} value={m.value}>{m.short}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-zinc-400 uppercase tracking-wider mb-1 block">Até</label>
                    <select
                      value={config.endMonth}
                      onChange={(e) => setConfig({ ...config, endMonth: parseInt(e.target.value) })}
                      className="h-10 px-4 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-emerald-500 focus:outline-none w-full"
                    >
                      {months.filter(m => m.value >= config.startMonth).map(m => (
                        <option key={m.value} value={m.value}>{m.short}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* PDF Format */}
            <Card className="glass-panel border-white/10">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-white flex items-center gap-2">
                  <FileText className="text-[#22C55E]" size={18} />
                  Formato do PDF
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                  config.pdfFormat === 'a4' 
                    ? 'border-[#22C55E]/50 bg-[#22C55E]/10' 
                    : 'border-white/10 hover:border-white/20'
                }`}>
                  <input
                    type="radio"
                    name="pdfFormat"
                    value="a4"
                    checked={config.pdfFormat === 'a4'}
                    onChange={(e) => setConfig({ ...config, pdfFormat: e.target.value })}
                    className="sr-only"
                  />
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    config.pdfFormat === 'a4' ? 'border-[#22C55E]' : 'border-zinc-500'
                  }`}>
                    {config.pdfFormat === 'a4' && <div className="w-2 h-2 rounded-full bg-[#22C55E]" />}
                  </div>
                  <Monitor size={18} className="text-zinc-400" />
                  <div>
                    <p className="text-sm text-white font-medium">Desktop (A4)</p>
                    <p className="text-[10px] text-zinc-500">Ideal para impressão e computador</p>
                  </div>
                </label>
                <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                  config.pdfFormat === 'mobile' 
                    ? 'border-[#22C55E]/50 bg-[#22C55E]/10' 
                    : 'border-white/10 hover:border-white/20'
                }`}>
                  <input
                    type="radio"
                    name="pdfFormat"
                    value="mobile"
                    checked={config.pdfFormat === 'mobile'}
                    onChange={(e) => setConfig({ ...config, pdfFormat: e.target.value })}
                    className="sr-only"
                  />
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    config.pdfFormat === 'mobile' ? 'border-[#22C55E]' : 'border-zinc-500'
                  }`}>
                    {config.pdfFormat === 'mobile' && <div className="w-2 h-2 rounded-full bg-[#22C55E]" />}
                  </div>
                  <Smartphone size={18} className="text-cyan-400" />
                  <div>
                    <p className="text-sm text-white font-medium">Mobile</p>
                    <p className="text-[10px] text-zinc-500">Otimizado para visualização no celular</p>
                  </div>
                </label>
              </CardContent>
            </Card>

            {/* Detail Level */}
            <Card className="glass-panel border-white/10">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-white flex items-center gap-2">
                  <Layers className="text-[#D4A43A]" size={18} />
                  Nível de Detalhe
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                  config.detailLevel === 'simple' 
                    ? 'border-[#22C55E]/50 bg-[#22C55E]/10' 
                    : 'border-white/10 hover:border-white/20'
                }`}>
                  <input
                    type="radio"
                    name="detailLevel"
                    value="simple"
                    checked={config.detailLevel === 'simple'}
                    onChange={(e) => setConfig({ ...config, detailLevel: e.target.value, monthByMonthDetail: false })}
                    className="sr-only"
                  />
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    config.detailLevel === 'simple' ? 'border-[#22C55E]' : 'border-zinc-500'
                  }`}>
                    {config.detailLevel === 'simple' && <div className="w-2 h-2 rounded-full bg-[#22C55E]" />}
                  </div>
                  <div>
                    <p className="text-sm text-white font-medium">Simplificado</p>
                    <p className="text-[10px] text-zinc-500">Resumo geral do período</p>
                  </div>
                </label>
                <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                  config.detailLevel === 'detailed' 
                    ? 'border-[#22C55E]/50 bg-[#22C55E]/10' 
                    : 'border-white/10 hover:border-white/20'
                }`}>
                  <input
                    type="radio"
                    name="detailLevel"
                    value="detailed"
                    checked={config.detailLevel === 'detailed'}
                    onChange={(e) => setConfig({ ...config, detailLevel: e.target.value })}
                    className="sr-only"
                  />
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    config.detailLevel === 'detailed' ? 'border-[#22C55E]' : 'border-zinc-500'
                  }`}>
                    {config.detailLevel === 'detailed' && <div className="w-2 h-2 rounded-full bg-[#22C55E]" />}
                  </div>
                  <div>
                    <p className="text-sm text-white font-medium">Detalhado</p>
                    <p className="text-[10px] text-zinc-500">Inclui categorias e origens</p>
                  </div>
                </label>

                {/* Month by month option */}
                {config.detailLevel === 'detailed' && (
                  <label className="flex items-center gap-3 p-3 rounded-lg border border-white/10 hover:border-white/20 cursor-pointer transition-all ml-4">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      config.monthByMonthDetail 
                        ? 'bg-[#22C55E] border-[#22C55E]' 
                        : 'border-zinc-600'
                    }`}>
                      {config.monthByMonthDetail && <Check size={12} className="text-black" />}
                    </div>
                    <div>
                      <p className="text-sm text-zinc-300">Breakdown mês a mês</p>
                      <p className="text-[10px] text-zinc-500">Página separada por mês</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={config.monthByMonthDetail}
                      onChange={(e) => setConfig({ ...config, monthByMonthDetail: e.target.checked })}
                      className="sr-only"
                    />
                  </label>
                )}
              </CardContent>
            </Card>

            {/* Include Options */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-white flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-zinc-800/50">
                    <List className="w-4 h-4 text-emerald-400" />
                  </div>
                  Incluir no Relatório
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {/* Seção: Relatórios Financeiros */}
                <p className="text-xs text-zinc-500 uppercase tracking-wider pt-2 pb-1 font-medium">Demonstrativos</p>
                {[
                  { key: 'includeDRE', label: 'DRE Mensal', desc: 'Tabela resumo', icon: BarChart3, color: 'text-emerald-400' },
                  { key: 'includeExpenseCategories', label: 'Despesas por Categoria', desc: 'Breakdown de gastos', icon: TrendingDown, color: 'text-red-400' },
                  { key: 'includeRevenueCategories', label: 'Receitas por Origem', desc: 'Fontes de faturamento', icon: TrendingUp, color: 'text-emerald-400' },
                ].map(({ key, label, desc, icon: Icon, color }) => (
                  <label key={key} className="flex items-center gap-3 p-2 rounded-lg hover:bg-zinc-800/50 cursor-pointer group transition-colors">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      config[key] 
                        ? 'bg-emerald-500 border-emerald-500' 
                        : 'border-zinc-600 group-hover:border-zinc-500'
                    }`}>
                      {config[key] && <Check size={12} className="text-black" />}
                    </div>
                    <Icon size={16} className={color} />
                    <div className="flex-1">
                      <span className="text-sm text-zinc-300">{label}</span>
                      <p className="text-xs text-zinc-600">{desc}</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={config[key]}
                      onChange={(e) => setConfig({ ...config, [key]: e.target.checked })}
                      className="sr-only"
                    />
                  </label>
                ))}
                
                {/* Seção: Fluxo e Projeções */}
                <p className="text-xs text-zinc-500 uppercase tracking-wider pt-4 pb-1 font-medium">Fluxo e Projeções</p>
                {[
                  { key: 'includeFluxoCaixa', label: 'Fluxo de Caixa', desc: 'Entradas e saídas mensais', icon: Wallet, color: 'text-blue-400' },
                  { key: 'includeFaturadoVsRecebido', label: 'Faturado vs Recebido', desc: 'Gap e carteira a receber', icon: ArrowRightLeft, color: 'text-amber-400' },
                  { key: 'includeProjecao', label: 'Projeção Financeira', desc: 'Cenários futuros', icon: Activity, color: 'text-cyan-400' },
                ].map(({ key, label, desc, icon: Icon, color }) => (
                  <label key={key} className="flex items-center gap-3 p-2 rounded-lg hover:bg-zinc-800/50 cursor-pointer group transition-colors">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      config[key] 
                        ? 'bg-emerald-500 border-emerald-500' 
                        : 'border-zinc-600 group-hover:border-zinc-500'
                    }`}>
                      {config[key] && <Check size={12} className="text-black" />}
                    </div>
                    <Icon size={16} className={color} />
                    <div className="flex-1">
                      <span className="text-sm text-zinc-300">{label}</span>
                      <p className="text-xs text-zinc-600">{desc}</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={config[key]}
                      onChange={(e) => setConfig({ ...config, [key]: e.target.checked })}
                      className="sr-only"
                    />
                  </label>
                ))}
                
                {/* Seção: Performance e Saúde */}
                <p className="text-xs text-zinc-500 uppercase tracking-wider pt-4 pb-1 font-medium">Performance e Saúde</p>
                {[
                  { key: 'includePerformance', label: 'Performance de Metas & Orçamento', desc: 'Acompanhamento de metas e tetos de gasto', icon: Target, color: 'text-emerald-400' },
                  { key: 'includePerformanceCharts', label: 'Gráficos Performance', desc: 'Metas e orçamentos mês a mês', icon: LineChart, color: 'text-amber-400' },
                  { key: 'includeSaudeFinanceira', label: 'Saúde Financeira', desc: 'Score e indicadores', icon: Activity, color: 'text-emerald-400' },
                  { key: 'includeCompromissos', label: 'Compromissos', desc: 'Parcelas e obrigações', icon: CreditCard, color: 'text-purple-400' },
                ].map(({ key, label, desc, icon: Icon, color }) => (
                  <label key={key} className="flex items-center gap-3 p-2 rounded-lg hover:bg-zinc-800/50 cursor-pointer group transition-colors">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      config[key] 
                        ? 'bg-emerald-500 border-emerald-500' 
                        : 'border-zinc-600 group-hover:border-zinc-500'
                    }`}>
                      {config[key] && <Check size={12} className="text-black" />}
                    </div>
                    <Icon size={16} className={color} />
                    <div className="flex-1">
                      <span className="text-sm text-zinc-300">{label}</span>
                      <p className="text-xs text-zinc-600">{desc}</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={config[key]}
                      onChange={(e) => setConfig({ ...config, [key]: e.target.checked })}
                      className="sr-only"
                    />
                  </label>
                ))}
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                onClick={loadDREData}
                disabled={loading || !selectedGym}
                className="w-full h-11 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-white text-sm"
              >
                {loading ? (
                  <Loader2 className="animate-spin mr-2" size={18} />
                ) : (
                  <Eye className="mr-2" size={18} />
                )}
                Visualizar Prévia
              </Button>
              
              <Button
                onClick={generatePDF}
                disabled={generating || !dreData}
                className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm"
              >
                {generating ? (
                  <Loader2 className="animate-spin mr-2" size={18} />
                ) : (
                  <Download className="mr-2" size={18} />
                )}
                Exportar PDF
              </Button>
              
              {/* Share via WhatsApp Button */}
              <Button
                onClick={shareViaWhatsApp}
                disabled={!lastGeneratedPdf}
                className={`w-full h-11 font-semibold text-sm transition-all ${
                  lastGeneratedPdf 
                    ? 'bg-green-600 hover:bg-green-700 text-white' 
                    : 'bg-zinc-700 text-zinc-400 cursor-not-allowed'
                }`}
                data-testid="share-whatsapp-button"
              >
                <MessageCircle className="mr-2" size={18} />
                Compartilhar via WhatsApp
              </Button>
              
              {lastGeneratedPdf && (
                <p className="text-xs text-zinc-500 text-center mt-1">
                  PDF pronto para compartilhar
                </p>
              )}
            </div>
          </div>

          {/* Preview Panel */}
          <div className="lg:col-span-2">
            <Card className="bg-zinc-900 border-zinc-800 h-full">
              <CardHeader className="pb-2 sm:pb-3">
                <CardTitle className="text-sm sm:text-base text-white flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-zinc-800/50">
                    <FileText className="w-4 h-4 text-emerald-400" />
                  </div>
                  Prévia do Relatório
                  {dreData && (
                    <span className="ml-auto text-xs text-zinc-500 font-normal">
                      {config.detailLevel === 'simple' ? 'Simplificado' : 'Detalhado'}
                      {config.monthByMonthDetail && ' • Mês a mês'}
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!dreData ? (
                  <div className="h-48 sm:h-80 flex flex-col items-center justify-center text-zinc-500">
                    <FileText className="mb-3 sm:mb-4 opacity-30 w-10 h-10 sm:w-12 sm:h-12" />
                    <p className="text-sm sm:text-base text-center px-4">Configure e clique em &ldquo;Visualizar Prévia&rdquo;</p>
                    <p className="text-[10px] sm:text-xs text-zinc-600 mt-1">Os dados serão carregados aqui</p>
                  </div>
                ) : (
                  <div className="space-y-4 sm:space-y-6 max-h-[400px] sm:max-h-[600px] overflow-y-auto custom-scrollbar pr-2">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-3 gap-2 sm:gap-3">
                      <div className="p-2 sm:p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                        <p className="text-[8px] sm:text-[10px] text-zinc-400 uppercase mb-0.5 sm:mb-1">Faturamento</p>
                        <p className="text-sm sm:text-lg font-bold text-emerald-400 font-numbers truncate">
                          {formatCurrency(dreData.summary.total_revenue)}
                        </p>
                      </div>
                      <div className="p-2 sm:p-3 rounded-xl bg-red-500/10 border border-red-500/20">
                        <p className="text-xs text-zinc-400 uppercase mb-1">Despesas</p>
                        <p className="text-lg font-bold text-red-400 font-numbers">
                          {formatCurrency(dreData.summary.total_expenses)}
                        </p>
                      </div>
                      <div className={`p-3 rounded-xl border ${
                        dreData.summary.total_profit >= 0 
                          ? 'bg-emerald-500/10 border-emerald-500/20' 
                          : 'bg-red-500/10 border-red-500/20'
                      }`}>
                        <p className="text-xs text-zinc-400 uppercase mb-1">Lucro</p>
                        <p className={`text-lg font-bold font-numbers ${
                          dreData.summary.total_profit >= 0 ? 'text-emerald-400' : 'text-red-400'
                        }`}>
                          {formatCurrency(dreData.summary.total_profit)}
                        </p>
                      </div>
                    </div>

                    {/* DRE Table */}
                    {config.includeDRE && (
                      <div>
                        <h3 className="text-sm text-zinc-400 uppercase mb-2 flex items-center gap-2">
                          <BarChart3 size={14} className="text-emerald-400" />
                          DRE Mensal
                        </h3>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="border-b border-zinc-700">
                                <th className="text-left py-2 px-2 text-zinc-400 font-medium">Mês</th>
                                <th className="text-right py-2 px-2 text-zinc-400 font-medium">Faturamento</th>
                                <th className="text-right py-2 px-2 text-zinc-400 font-medium">Despesas</th>
                                <th className="text-right py-2 px-2 text-zinc-400 font-medium">Lucro</th>
                                <th className="text-center py-2 px-2 text-zinc-400 font-medium">Margem</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dreData.monthly_data.map((month) => (
                                <tr key={month.month} className="border-b border-zinc-800 hover:bg-zinc-800/50">
                                  <td className="py-2 px-2 text-white capitalize">{month.month_short}</td>
                                  <td className="py-2 px-2 text-right text-emerald-400 font-numbers">
                                    {formatCurrency(month.revenue)}
                                  </td>
                                  <td className="py-2 px-2 text-right text-red-400 font-numbers">
                                    {formatCurrency(month.expenses)}
                                  </td>
                                  <td className={`py-2 px-2 text-right font-numbers ${
                                    month.profit >= 0 ? 'text-emerald-400' : 'text-red-400'
                                  }`}>
                                    {formatCurrency(month.profit)}
                                  </td>
                                  <td className="py-2 px-2 text-center text-zinc-400">
                                    {month.margin.toFixed(1)}%
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="bg-zinc-800/50 font-bold">
                                <td className="py-2 px-2 text-white">TOTAL</td>
                                <td className="py-2 px-2 text-right text-emerald-400 font-numbers">
                                  {formatCurrency(dreData.summary.total_revenue)}
                                </td>
                                <td className="py-2 px-2 text-right text-red-400 font-numbers">
                                  {formatCurrency(dreData.summary.total_expenses)}
                                </td>
                                <td className={`py-2 px-2 text-right font-numbers ${
                                  dreData.summary.total_profit >= 0 ? 'text-emerald-400' : 'text-red-400'
                                }`}>
                                  {formatCurrency(dreData.summary.total_profit)}
                                </td>
                                <td className="py-2 px-2 text-center text-zinc-300">
                                  {dreData.summary.overall_margin.toFixed(1)}%
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Categories Preview */}
                    {config.detailLevel === 'detailed' && (
                      <div className="grid grid-cols-2 gap-4">
                        {/* Expense Categories */}
                        {config.includeExpenseCategories && Object.keys(dreData.summary.expense_categories).length > 0 && (
                          <div>
                            <h3 className="text-sm text-zinc-400 uppercase mb-2 flex items-center gap-2">
                              <TrendingDown size={14} className="text-red-400" />
                              Despesas por Categoria
                            </h3>
                            <div className="space-y-1">
                              {Object.entries(dreData.summary.expense_categories)
                                .sort((a, b) => b[1] - a[1])
                                .slice(0, 6)
                                .map(([category, amount]) => (
                                  <div key={category} className="flex justify-between items-center text-xs p-2 rounded bg-zinc-800/50">
                                    <span className="text-zinc-400">{category}</span>
                                    <span className="text-red-400 font-numbers">{formatCurrency(amount)}</span>
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}

                        {/* Revenue Sources */}
                        {config.includeRevenueCategories && Object.keys(dreData.summary.revenue_sources).length > 0 && (
                          <div>
                            <h3 className="text-sm text-zinc-400 uppercase mb-2 flex items-center gap-2">
                              <TrendingUp size={14} className="text-emerald-400" />
                              Receitas por Origem
                            </h3>
                            <div className="space-y-1">
                              {Object.entries(dreData.summary.revenue_sources)
                                .sort((a, b) => b[1] - a[1])
                                .slice(0, 6)
                                .map(([source, amount]) => (
                                  <div key={source} className="flex justify-between items-center text-xs p-2 rounded bg-zinc-800/50">
                                    <span className="text-zinc-400">{source}</span>
                                    <span className="text-emerald-400 font-numbers">{formatCurrency(amount)}</span>
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Month by month indicator */}
                    {config.monthByMonthDetail && (
                      <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                        <p className="text-xs text-emerald-400 flex items-center gap-2">
                          <Layers size={14} />
                          O PDF terá uma página detalhada para cada mês com dados
                        </p>
                      </div>
                    )}

                    {/* Performance charts indicator */}
                    {config.includePerformanceCharts && (
                      <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                        <p className="text-xs text-amber-400 flex items-center gap-2">
                          <LineChart size={14} />
                          O PDF incluirá gráficos de Performance de Metas mês a mês
                        </p>
                      </div>
                    )}

                    {/* Novos indicadores de relatórios */}
                    {config.includeFluxoCaixa && (
                      <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                        <p className="text-xs text-blue-400 flex items-center gap-2">
                          <Wallet size={14} />
                          Incluirá tabela de Fluxo de Caixa com entradas e saídas por mês
                        </p>
                      </div>
                    )}

                    {config.includeFaturadoVsRecebido && (
                      <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                        <p className="text-xs text-amber-400 flex items-center gap-2">
                          <ArrowRightLeft size={14} />
                          Incluirá comparativo Faturado vs Recebido, Gap e Carteira a Receber
                        </p>
                      </div>
                    )}

                    {config.includeProjecao && (
                      <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
                        <p className="text-xs text-cyan-400 flex items-center gap-2">
                          <Activity size={14} />
                          Incluirá projeção financeira com cenários (otimista, realista, pessimista)
                        </p>
                      </div>
                    )}

                    {config.includeSaudeFinanceira && (
                      <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                        <p className="text-xs text-emerald-400 flex items-center gap-2">
                          <Activity size={14} />
                          Incluirá Score de Saúde Financeira e KPIs principais
                        </p>
                      </div>
                    )}

                    {config.includeCompromissos && (
                      <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
                        <p className="text-xs text-purple-400 flex items-center gap-2">
                          <CreditCard size={14} />
                          Incluirá lista de compromissos e parcelas pendentes
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ReportsPage;
